import React, { useEffect, useState } from 'react';
import { create } from 'zustand';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { useApptekster } from '../../contexts/ApptekstContext';
import { useApi } from '../../hooks/useApi';
import { useGyldigeSkiltformater } from '../../hooks/useGyldigeSkiltformater';
import {
    hentTegnkombinasjonStatusInnlogget,
    hentGyldigeKjoretoy,
    sendSoknad,
    SoknadStatus,
} from '../../utils/api';
import { Skilt, Skiltstorrelse } from '../../components/skilt/Skilt';
import {
    TegnkombinasjonSkjema,
    TegnkombinasjonSkjemaData,
} from '../../components/tegnkombinasjon-skjema/TegnkombinasjonSkjema';
import { Paragraph } from '../../components/Paragraph';
import { SVVBreadcrumbLink } from '@komponentkassen/svv-breadcrumbs';
import { HotjarMaskert } from '../../components/HotjarMaskert';
import { SVVSpinner } from '@komponentkassen/svv-spinner';
import { SVVLayoutBanner } from '@komponentkassen/svv-layout';
import { SVVInformationMessage } from '@komponentkassen/svv-information-message';
import { GodtaVilkarSkjema } from '../../components/GodtaVilkarSkjema';
import { SVVTextLink } from '@komponentkassen/svv-link';
type SoknadStatusMedTegnkombinasjon = SoknadStatus & {
    tegnkombinasjon: string;
};

type SoknadStatusState = {
    soknadStatus?: SoknadStatusMedTegnkombinasjon;
    setSoknadStatus: (soknadStatus: SoknadStatusMedTegnkombinasjon) => void;
};

export const useSoknadStatusState = create<SoknadStatusState>((set) => ({
    soknadStatus: undefined,
    setSoknadStatus: (soknadStatus: SoknadStatusMedTegnkombinasjon) =>
        set(() => ({ soknadStatus })),
}));

export const Soknad: React.FC = () => {
    const t = useApptekster();
    const navigate = useNavigate();
    const [sendtTegnkombinasjon, setSendtTegnkombinasjon] = useState('');
    const [tegnkombinasjonStatusSendt, setTegnkombinasjonStatusSendt] =
        useState(false);
    const { tegnkombinasjon } = useParams<{ tegnkombinasjon: string }>();
    const tegnkombinasjonStatusApi = useApi(hentTegnkombinasjonStatusInnlogget);
    const gyldigeKjoretoyApi = useApi(hentGyldigeKjoretoy);
    const sendSoknadApi = useApi(sendSoknad, { id: 'sendSoknad' });
    const skjema = useForm<TegnkombinasjonSkjemaData>({
        defaultValues: { sokefelt: tegnkombinasjon },
    });

    const gyldigeSkiltformater = useGyldigeSkiltformater(
        tegnkombinasjonStatusApi.data?.gyldigeSkiltformat,
    );
    const setSoknadStatus = useSoknadStatusState(
        (state) => state.setSoknadStatus,
    );

    const sperreSoknad =
        tegnkombinasjonStatusApi.data?.melding === 'kan_ikke_soke' ||
        tegnkombinasjonStatusApi.data?.melding === 'mangler_kjoretoy' ||
        tegnkombinasjonStatusApi.data?.melding === 'kjoretoy_med_medeier' ||
        tegnkombinasjonStatusApi.data?.melding === 'for_mange_soknader';

    const visResultat =
        tegnkombinasjonStatusApi.data?.ok &&
        !skjema.formState.isDirty &&
        gyldigeKjoretoyApi.data;

    useEffect(() => {
        if (tegnkombinasjonStatusApi.data?.ok && tegnkombinasjonStatusSendt) {
            gyldigeKjoretoyApi.hent(sendtTegnkombinasjon);
            setSendtTegnkombinasjon('');
            setTegnkombinasjonStatusSendt(false);
        }
    }, [
        tegnkombinasjonStatusApi.data,
        gyldigeKjoretoyApi,
        tegnkombinasjonStatusSendt,
        sendtTegnkombinasjon,
    ]);

    useEffect(() => {
        if (sendSoknadApi.data?.ok) {
            setSoknadStatus({
                ...sendSoknadApi.data,
                tegnkombinasjon: tegnkombinasjonStatusApi.data!.tegnkombinasjon,
            });
            navigate('/soknad-bekreftelse');
        }
    }, [
        tegnkombinasjonStatusApi,
        sendSoknadApi.data,
        navigate,
        setSoknadStatus,
        tegnkombinasjon,
    ]);

    return (
        <>
            <SVVLayoutBanner>
                <SVVBreadcrumbLink
                    href={'#/'}
                    ariaLabel={t('personlig-kjennemerke.snarvei.meny')}
                    className="intern-link"
                >
                    {t('personlig-kjennemerke.snarvei.meny')}
                </SVVBreadcrumbLink>
                <h1 className="spacing-bottom--small">
                    {t('personlig-kjennemerke.soknad.overskrift')}
                </h1>
                <p className="svv-ingress spacing-bottom--medium">
                    {t('personlig-kjennemerke.soknad.ingress')}{' '}
                    <SVVTextLink
                        url="https://www.vegvesen.no/kjoretoy/eie-og-vedlikeholde/skilt/personlig-bilskilt/"
                        text={t('personlig-kjennemerke.soknad.les-mer')}
                    />
                </p>

                <TegnkombinasjonSkjema
                    variant="innlogget"
                    api={tegnkombinasjonStatusApi}
                    skjema={skjema}
                    tegnkombinasjon={tegnkombinasjon}
                    onSubmit={(tegnkombinasjon) => {
                        setSendtTegnkombinasjon(tegnkombinasjon);
                        setTegnkombinasjonStatusSendt(true);
                    }}
                />

                <div aria-live="polite">
                    {(tegnkombinasjonStatusApi.laster ||
                        gyldigeKjoretoyApi.laster) && (
                        <div className="tekstbredde responsive-spacing-top--medium">
                            <SVVSpinner>
                                {t('personlig-kjennemerke.delt.laster')}
                            </SVVSpinner>
                        </div>
                    )}
                </div>
            </SVVLayoutBanner>

            {visResultat && (
                <SVVLayoutBanner secondary>
                    <SVVInformationMessage type={'success'}>
                        <HotjarMaskert>
                            {tegnkombinasjonStatusApi.data!.tegnkombinasjon}
                        </HotjarMaskert>
                        {t(
                            'personlig-kjennemerke.soknad.tegnkombinasjon.ledig',
                        )}
                        !
                    </SVVInformationMessage>
                    {gyldigeSkiltformater.includes('SH') && (
                        <div className="spacing-bottom--small spacing-top--medium">
                            <Paragraph>
                                {t(
                                    'personlig-kjennemerke.soknad.alle-storrelser',
                                )}
                            </Paragraph>
                        </div>
                    )}
                    {!gyldigeSkiltformater.includes('SH') && (
                        <div className="spacing-bottom--small spacing-top--medium">
                            <Paragraph>
                                {t(
                                    'personlig-kjennemerke.soknad.kun-store-skilt',
                                )}
                            </Paragraph>
                        </div>
                    )}
                    <div className="spacing-bottom--small spacing-top--medium">
                        <div className="skilt-eksempel">
                            <Skilt
                                tegnkombinasjon={
                                    tegnkombinasjonStatusApi.data!
                                        .tegnkombinasjon
                                }
                            />

                            {gyldigeSkiltformater.includes('SH') && (
                                <Skilt
                                    storrelse={Skiltstorrelse.SH}
                                    tegnkombinasjon={
                                        tegnkombinasjonStatusApi.data!
                                            .tegnkombinasjon
                                    }
                                />
                            )}
                        </div>
                    </div>
                    {tegnkombinasjonStatusApi.data?.melding ===
                        'kan_ikke_soke' && (
                        <SVVInformationMessage type="warning">
                            {t(
                                'personlig-kjennemerke.soknad.validering.kan-ikke-soke',
                            )}
                        </SVVInformationMessage>
                    )}
                    {tegnkombinasjonStatusApi.data?.melding ===
                        'kjoretoy_med_medeier' && (
                        <SVVInformationMessage type="warning">
                            {t(
                                'personlig-kjennemerke.soknad.validering.kjoretoy-med-medeier',
                            )}
                        </SVVInformationMessage>
                    )}
                    {tegnkombinasjonStatusApi.data?.melding ===
                        'for_mange_soknader' && (
                        <SVVInformationMessage type="warning">
                            {t(
                                'personlig-kjennemerke.soknad.validering.for-mange-soknader',
                            )}
                        </SVVInformationMessage>
                    )}
                    {tegnkombinasjonStatusApi.data?.melding ===
                        'mangler_kjoretoy' && (
                        <SVVInformationMessage type="warning">
                            {t(
                                'personlig-kjennemerke.soknad.validering.mangler-kjoretoy',
                            )}
                        </SVVInformationMessage>
                    )}

                    {!sperreSoknad && (
                        <>
                            {gyldigeKjoretoyApi.data!.length > 0 && (
                                <div className="spacing-bottom--xsmall">
                                    <h3 className="svv-h4 svv-h4--spacing">
                                        {t(
                                            'personlig-kjennemerke.soknad.gyldige-kjoretoy',
                                        )}
                                    </h3>
                                    <ul>
                                        <HotjarMaskert>
                                            {gyldigeKjoretoyApi.data!.map(
                                                (gyldigeKjoretoy) => (
                                                    <li
                                                        className="p"
                                                        key={
                                                            gyldigeKjoretoy.kjennemerke
                                                        }
                                                    >
                                                        {
                                                            gyldigeKjoretoy.kjennemerke
                                                        }{' '}
                                                        {
                                                            gyldigeKjoretoy.merkenavn
                                                        }{' '}
                                                        {gyldigeKjoretoy.modell}{' '}
                                                        (
                                                        {
                                                            gyldigeKjoretoy.arsmodell
                                                        }
                                                        )
                                                    </li>
                                                ),
                                            )}
                                        </HotjarMaskert>
                                    </ul>
                                </div>
                            )}
                            <div>
                                {gyldigeKjoretoyApi.data!.length === 0 && (
                                    <Paragraph>
                                        {t(
                                            'personlig-kjennemerke.soknad.ingen-kjoretoy',
                                        )}
                                    </Paragraph>
                                )}
                                <Paragraph>
                                    {t(
                                        'personlig-kjennemerke.soknad.rettighet',
                                    )}
                                </Paragraph>
                            </div>
                            <GodtaVilkarSkjema
                                bekreftelseslabel={t(
                                    'personlig-kjennemerke.soknad.bekreftelse',
                                )}
                                bekreftelsestekst={t(
                                    'personlig-kjennemerke.soknad.vilkar',
                                )}
                                handlingsknapp={t(
                                    'personlig-kjennemerke.soknad.sok',
                                )}
                                onSubmit={() =>
                                    sendSoknadApi.hent(
                                        tegnkombinasjonStatusApi.data
                                            ?.tegnkombinasjon,
                                    )
                                }
                                laster={sendSoknadApi.laster}
                                avbrytKnapp={() => navigate('/')}
                            />
                        </>
                    )}
                </SVVLayoutBanner>
            )}
        </>
    );
};
